import { runPrograms } from './runs'
/**
 * The brief you would hand an agent to go and fix a failed rollout.
 *
 * Everything here is already on the page — the run's configuration, the attempt
 * records, the failure vocabulary in `failures.ts`. Nothing is fetched, so the
 * button works against any backend, released or not. What the reader cannot get
 * from the page is *where the code lives*, so naming the two repositories that
 * own the failing path is the part that earns the button.
 */
import { additionalFailures, describeFailure, attemptFailureCode, attemptFailureMessage, describeRetry } from '@/lib/failures'
import type { Attempt, NodeDetail, RunConfiguration, RunNode, Rollout } from '@/lib/runs'
import { failedAttempts } from '@/lib/runs'

/** Enough of the log to diagnose from, small enough to paste into a chat. Head and
 *  tail both survive: a traceback names the error at the end and the call that
 *  started it at the beginning, and eliding the middle loses the least. */
export const LOG_BUDGET = 4000

export function clampLog(text: string, budget = LOG_BUDGET): string {
  const value = text.trim()
  if (value.length <= budget) return value
  const head = Math.floor(budget / 3)
  const tail = budget - head
  const dropped = value.length - budget
  return `${value.slice(0, head).trimEnd()}\n\n…[${dropped.toLocaleString()} characters elided]…\n\n${value.slice(-tail).trimStart()}`
}

/** The framework decides which harness repository an agent has to open. The
 *  controller is always in scope: it owns task delivery and the node agent. */
function repositories(framework: RunConfiguration['framework']): string[] {
  const harness = framework === 'osworld2'
    ? 'Seldon-Foundation/seldon-fusion-env-os-world — the OSWorld harness bundle the node runs (runner/), layered over upstream OSWorld-V2 at the pinned commit'
    : 'Seldon-Foundation/gym-anything — the GymAnything framework and its harnesses (agents/harnesses/)'
  return [
    'Seldon-Foundation/seldon-rl-env-cua — the experiment controller, node agent and task delivery (experiment_ctl/)',
    harness,
  ]
}

function attemptLines(attempt: Attempt, ordinal: number, total?: number): string[] {
  const failure = describeFailure(attemptFailureCode(attempt))
  const lines = [total == null ? `Attempt ${ordinal}` : `Attempt ${ordinal} of ${total}`]
  if (attempt.attempt_id) lines.push(`  attempt_id ${attempt.attempt_id}`)
  if (attempt.diagnostic?.diagnostic_id) lines.push(`  diagnostic_id ${attempt.diagnostic.diagnostic_id}`)
  if (failure) {
    lines.push(`  class      ${failure.raw} — ${failure.label} (${failure.layer})`)
    lines.push(`  means      ${failure.meaning}`)
    if (attemptFailureMessage(attempt)) lines.push(`  message    ${attemptFailureMessage(attempt)}`)
    const retry = describeRetry(attempt)
    if (retry) lines.push(`  recovery   ${retry.label} — ${retry.meaning}`)
  } else {
    lines.push(`  class      ${attempt.error_class ?? 'unrecorded'}`)
  }
  const verdict = [
    attempt.sample_outcome && `sample ${attempt.sample_outcome}`,
    attempt.retry_decision && `retry ${attempt.retry_decision}`,
    attempt.node_action && `node ${attempt.node_action}`,
  ].filter(Boolean)
  if (verdict.length) lines.push(`  verdict    ${verdict.join(' · ')}`)
  const additional = additionalFailures(attempt)
  if (additional.length) {
    lines.push('  additional failures (same attempt, after the primary one; the class above is the cause):')
    for (const row of additional) {
      lines.push(`    - ${row.code}${row.stage ? ` [${row.stage}]` : ''}${row.message ? ` — ${row.message}` : ''}`)
      if (row.diagnostic_id) lines.push(`      diagnostic_id ${row.diagnostic_id}`)
      if (row.exception_type) lines.push(`      exception    ${row.exception_type}`)
    }
  }
  return lines
}

export function failurePrompt({ runId, runName, configuration, rollout, taskRollouts }: {
  runId: string; runName: string; configuration: RunConfiguration
  rollout: Rollout; taskRollouts: number
}): string {
  const failures = failedAttempts(rollout.attempts)
  const logs = failures
    .map(attempt => {
      const ordinal = rollout.attempts.indexOf(attempt) + 1
      return attempt.detail ? `--- attempt ${ordinal} ---\n${attempt.detail}` : ''
    })
    .filter(Boolean)
    .join('\n\n')

  return [
    'A rollout in one of our reinforcement-learning experiments failed. Work out why and fix it.',
    '',
    'A rollout is one attempt at one CAD task: a model drives a desktop VM through a harness,',
    'the episode is recorded, and a verifier scores the result. A failure is either the task',
    'definition, the harness, the model provider, or the node — the error class below says',
    'which layer the runner blamed, and that is a claim to check rather than to trust.',
    '',
    'The code lives in two repositories:',
    ...repositories(configuration.framework).map(line => `  - ${line}`),
    '',
    `Task      ${rollout.task_id}`,
    `Run       ${runName} (${runId})`,
    `Job       ${rollout.job_id} — rollout ${rollout.replicate + 1} of ${taskRollouts}, seed ${rollout.seed}`,
    `Program   ${configuration.task_references?.[rollout.task_id]?.program ?? configuration.program} @ ${configuration.task_references?.[rollout.task_id]?.commit ?? configuration.dataset_revision}`,
    `Stack     ${configuration.framework} · harness ${configuration.harness} · model ${configuration.model}`,
    `Budget    ${rollout.max_steps ?? configuration.max_steps} steps`,
    ...(rollout.instance_id ? [`Node      ${rollout.instance_id}`] : []),
    '',
    ...failures.flatMap((attempt, index) =>
      [...attemptLines(attempt, rollout.attempts.indexOf(attempt) + 1, rollout.attempts.length),
        ...(index === failures.length - 1 ? [] : [''])]),
    '',
    'Error log:',
    '```',
    logs ? clampLog(logs) : '(the attempt records carry no detail)',
    '```',
  ].join('\n')
}


/**
 * The same brief, for a worker node rather than a rollout.
 *
 * A node failure is not one job's problem: the same instance takes the next job
 * and breaks that one too. What an agent needs is the node's state, everything
 * it is answerable for, and its own log — which is the only place an
 * infrastructure failure explains itself.
 */
export function nodePrompt({ runId, runName, configuration, node, detail }: {
  runId: string; runName: string; configuration: RunConfiguration
  node: RunNode; detail?: NodeDetail
}): string {
  const failures = detail?.failures ?? []
  const logs = (detail?.logs ?? []).map(log => `--- ${log.title} ---\n${log.text}`).join('\n\n')
  return [
    'A worker node in one of our reinforcement-learning experiments is failing. Work out why and fix it.',
    '',
    'A node is one EC2 instance: it stages the pinned program image, polls the job',
    'queue, and runs up to its slot count of rollouts at a time. A node-level failure',
    'takes down every job that lands on it, so the node — not the job — is the unit here.',
    '',
    'The code lives in two repositories:',
    ...repositories(configuration.framework).map(line => `  - ${line}`),
    '',
    `Node      ${node.instance_id} — ${node.state}`,
    `Activity  ${node.activity}`,
    `Run       ${runName} (${runId})`,
    `Programs  ${runPrograms(configuration)}`,
    `Stack     ${configuration.framework} · harness ${configuration.harness} · model ${configuration.model}`,
    `Slots     ${node.busy}/${node.slots} busy`,
    `Counters  ${node.completed} completed · ${node.infra_total} infra failures · ${node.rate_limited} rate-limited · streak ${node.infra_streak}/10`,
    `Heartbeat ${node.age_seconds == null ? 'never' : `${Math.round(node.age_seconds)}s ago`}`,
    '',
    `Failed attempts on this node (${failures.length}):`,
    ...(failures.length
      ? failures.flatMap(row => [
          `  ${row.job_id} — task ${row.task_id ?? 'unknown'}`,
          ...attemptLines(row.attempt, row.ordinal).map(line => `  ${line}`),
        ])
      : ['  (none attributable to this node from its log)']),
    '',
    'Node log:',
    '```',
    logs ? clampLog(logs) : '(the node published no log)',
    '```',
  ].join('\n')
}
